import {
	type CountryCode,
	type PhoneNumber,
	parsePhoneNumber,
} from 'libphonenumber-js'

/**
 * Display a parsed number in the selected country's standard format. NANP (+1)
 * numbers use the national format with the country code — "+1 (519) 928-5525"
 * — and every other country keeps libphonenumber's canonical international
 * format (which already carries the country's own spacing/hyphens). The library
 * decides the layout; we don't impose a custom one.
 */
function formatForDisplay(parsed: PhoneNumber): string {
	if (parsed.countryCallingCode === '1') {
		return `+1 ${parsed.formatNational()}`
	}
	return parsed.formatInternational()
}

/**
 * Format phone number for display
 * Formats NANP numbers as "+1 (519) 928-5525" and other numbers in
 * international format (e.g., +44 20 7946 0958).
 * Falls back to original value if parsing fails
 */
export function formatPhoneNumber(
	phoneNumber: unknown,
	countryCode?: unknown,
): string | null {
	if (!phoneNumber || typeof phoneNumber !== 'string' || !phoneNumber.trim()) {
		return null
	}

	try {
		// Try to parse the phone number
		const parsed = parsePhoneNumber(phoneNumber)

		// If we successfully parsed and have a number, format it
		// Check if parsed exists and has a number property (more lenient than isValid())
		if (parsed?.number) {
			try {
				return formatForDisplay(parsed)
			} catch {
				// If formatting fails, return the number in E.164 format
				return parsed.number
			}
		}

		// If parsing fails or no number, try with country code hint
		if (countryCode && typeof countryCode === 'string') {
			try {
				const parsedWithCountry = parsePhoneNumber(
					phoneNumber,
					countryCode as unknown as CountryCode,
				)
				if (parsedWithCountry?.number) {
					try {
						return formatForDisplay(parsedWithCountry)
					} catch {
						return parsedWithCountry.number
					}
				}
			} catch {
				// Fall through to return original
			}
		}
		// Fallback: return original if parsing fails
		return phoneNumber
	} catch {
		// If all parsing fails, return the original value
		return phoneNumber
	}
}

/**
 * Format phone number for display with fallback
 * Returns formatted phone number or fallback string (default: '-')
 */
export function formatPhoneNumberWithFallback(
	phoneNumber: unknown,
	countryCode?: unknown,
	fallback = '-',
): string {
	const formatted = formatPhoneNumber(phoneNumber, countryCode)
	return formatted || fallback
}
